/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

// Il percorso da passare a `APIService.setIgnorePath()`: su queste API i 422
// e i 400 non diventano un toast dell'interceptor globale, perché la pagina
// mostra il messaggio accanto al modulo che l'ha provocato.
export const NOTE_SPESE_API_PATH = '/api/v2/note-spese/';

// Il messaggio generico del core per un parametro rifiutato dal validatore
// (`InvalidParamException::DEFAULT_ERROR_MESSAGE`): in quel caso si elencano
// i campi in italiano invece di mostrare l'inglese.
const DEFAULT_INVALID_PARAM_MESSAGE = 'Invalid Parameter';

const FIELD_LABELS: Record<string, string> = {
  date: 'data',
  vehicleId: 'veicolo',
  origin: 'partenza',
  destination: 'arrivo',
  from: 'partenza',
  to: 'arrivo',
  roundTrip: 'andata e ritorno',
  purpose: 'motivo',
  category: 'categoria',
  amount: 'importo',
  description: 'descrizione',
  receipt: 'ricevuta',
  removeReceipt: 'ricevuta',
  text: 'indirizzo',
};

interface ErrorBody {
  error?: {
    status?: string;
    message?: string;
    data?: {invalidParamKeys?: string[]};
  };
}

interface ResponseLike {
  status?: number;
  data?: ErrorBody;
}

// Con `setIgnorePath` l'interceptor rifiuta con la sola risposta axios invece
// che con l'errore che la contiene: si accettano entrambe le forme.
function responseOf(error: unknown): ResponseLike | null {
  if (!error || typeof error !== 'object') return null;
  const e = error as {response?: ResponseLike; status?: number; data?: unknown};
  if (e.response) return e.response;
  if (typeof e.status === 'number' && e.data !== undefined) {
    return e as ResponseLike;
  }
  return null;
}

export function isValidationError(error: unknown): boolean {
  const status = responseOf(error)?.status;
  return status === 422 || status === 400;
}

// Nessuna risposta: rete assente (in auto, in galleria) o server spento.
// L'interceptor mostra solo un generico "errore imprevisto".
export function isNetworkError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    responseOf(error) === null &&
    (error as {code?: string}).code !== 'ERR_CANCELED'
  );
}

/**
 * Il messaggio da mostrare sotto un modulo, o null se l'errore è già stato
 * mostrato dall'interceptor globale (toast per 403, 404, 500...).
 */
export function inlineErrorMessage(error: unknown): string | null {
  if (isNetworkError(error)) {
    return 'Connessione assente o server non raggiungibile: riprova tra poco. I dati inseriti sono ancora qui.';
  }
  if (!isValidationError(error)) return null;

  const body = responseOf(error)?.data?.error;
  const message = body?.message;
  if (message && message !== DEFAULT_INVALID_PARAM_MESSAGE) return message;

  const fields = (body?.data?.invalidParamKeys || [])
    .map((key) => FIELD_LABELS[key] || key)
    .filter((label, i, all) => all.indexOf(label) === i);
  return fields.length > 0
    ? `Controlla questi campi: ${fields.join(', ')}.`
    : 'Alcuni dati non sono validi: controlla il modulo.';
}
